import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Phone, MessageSquare, Mail, PhoneCall, X } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { api } from '@/lib/api.js';
import TaskRow from './TaskRow.jsx';
import LogCallSheet from './LogCallSheet.jsx';
import { formatDateOrEmpty } from '@/lib/date-display.js';

export default function ConversationMode({ onRefreshPattern }) {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logCallOpen, setLogCallOpen] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    api.get(`/api/tracon/command-center/pattern/conversation/${encodeURIComponent(contactId)}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [contactId]);

  function handleClose() {
    navigate('/traffic');
  }

  function handleLogCallSuccess() {
    setLogCallOpen(false);
    onRefreshPattern?.();
    // Refresh conversation data
    api.get(`/api/tracon/command-center/pattern/conversation/${encodeURIComponent(contactId)}`)
      .then(setData)
      .catch(() => {});
  }

  if (!contactId) return null;

  const contact = data?.contact || {};
  const tasks = data?.open_tasks || [];
  const activity = data?.recent_activity || [];

  return (
    <>
      <div className="fixed inset-y-0 right-0 z-40 w-full border-l border-border bg-surface shadow-2xl md:w-[600px] overflow-y-auto animate-in slide-in-from-right">
        <div className="sticky top-0 z-10 border-b border-border bg-surface px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-text">{contact.name || 'Loading…'}</h2>
              {contact.role_tags?.length > 0 && (
                <div className="mt-0.5 flex gap-1">
                  {contact.role_tags.map(tag => (
                    <span key={tag} className="rounded-full bg-border/40 px-2 py-0.5 text-[10px] text-text-muted">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-9 w-9 items-center justify-center rounded-full text-text-dim hover:bg-border/40"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>

          {!loading && (
            <div className="mt-3 flex flex-wrap gap-2">
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-text hover:bg-border/20">
                  <Phone size={12} strokeWidth={1.5} />
                  Call
                </a>
              )}
              {contact.phone && (
                <a href={`sms:${contact.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-text hover:bg-border/20">
                  <MessageSquare size={12} strokeWidth={1.5} />
                  SMS
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-text hover:bg-border/20">
                  <Mail size={12} strokeWidth={1.5} />
                  Email
                </a>
              )}
              <button
                type="button"
                onClick={() => setLogCallOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 border border-accent/30 px-3 py-1.5 text-xs text-accent hover:bg-accent/20"
              >
                <PhoneCall size={12} strokeWidth={1.5} />
                Log call
              </button>
            </div>
          )}
        </div>

        <div className="p-4 space-y-6">
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[0, 1, 2].map(i => (
                <div key={i} className="h-16 rounded-md border border-border bg-border/20" />
              ))}
            </div>
          ) : (
            <>
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                  Open work ({tasks.length})
                </h3>
                {tasks.length === 0 ? (
                  <p className="text-xs italic text-text-dim">No open tasks with this contact.</p>
                ) : (
                  <div className="space-y-2">
                    {tasks.map(item => (
                      <TaskRow key={item.zoho_task_id} item={item} onMutate={onRefreshPattern} />
                    ))}
                  </div>
                )}
              </section>

              {activity.length > 0 && (
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                    Recent activity
                  </h3>
                  <div className="space-y-1.5">
                    {activity.map(a => (
                      <div key={a.id} className="rounded border border-border/60 px-3 py-2 text-xs">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-medium text-text">{a.summary}</span>
                          <span className="shrink-0 text-[10px] text-text-dim">
                            {formatDateOrEmpty(a.created_at || a.occurred_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-border bg-surface p-4">
          <Button variant="outline" className="w-full" onClick={handleClose}>
            Done with this conversation
          </Button>
        </div>
      </div>

      <LogCallSheet
        open={logCallOpen}
        onOpenChange={setLogCallOpen}
        contactId={contactId}
        contactName={contact.name}
        dealOptions={tasks}
        onSuccess={handleLogCallSuccess}
      />
    </>
  );
}
